'use client';

import {
  createContext,
  type CSSProperties,
  type ReactNode,
  useContext,
  useEffect,
  useId,
  useRef,
} from 'react';
import { Icon } from '../icons';
import { cn } from '../../lib/utils';
import { Button, type ButtonProps } from './Button';
import {
  useOptionalDialogSurfaceTheme,
  type DialogSurfaceTheme,
} from './dialog-surface-theme';

export type DialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
  /** Classes on the native `<dialog>` element (backdrop + panel shell) */
  className?: string;
  /**
   * Use when the dialog has no visible `DialogTitle` so screen readers still get a name.
   * If omitted, `aria-labelledby` points at the id shared with `DialogTitle`.
   */
  'aria-label'?: string;
  /**
   * When true, Escape does not dismiss the modal (native `cancel` is prevented).
   * Use for required flows; still close programmatically via `onOpenChange(false)` / `open`.
   */
  preventDismiss?: boolean;
  /**
   * Theme scope on the native `<dialog>` (top layer). Prefer this over context when
   * the modal must match a specific palette regardless of portal inheritance.
   */
  appTheme?: DialogSurfaceTheme;
  /**
   * Inline `--theme-*` on the `<dialog>` (required for `adopt-quiz` shelter palettes;
   * see `AdoptPublicQuizThemeRoot` / `MatchShareResultsFlow`).
   */
  surfaceThemeStyle?: CSSProperties;
};

type DialogContextValue = {
  onOpenChange: (open: boolean) => void;
  titleId: string;
  descriptionId: string;
};

const DialogContext = createContext<DialogContextValue | null>(null);

function useDialogContext(component: string): DialogContextValue {
  const ctx = useContext(DialogContext);
  if (!ctx) {
    throw new Error(`${component} must be used inside <Dialog>`);
  }
  return ctx;
}

type DialogLayoutContextValue = {
  stickyFooter: boolean;
};

const DialogLayoutContext = createContext<DialogLayoutContextValue | null>(
  null,
);

function useDialogLayout(): DialogLayoutContextValue {
  const ctx = useContext(DialogLayoutContext);
  return ctx ?? { stickyFooter: false };
}

export type DialogContentProps = {
  children: ReactNode;
  className?: string;
  /** Renders a top-right dismiss control */
  showCloseButton?: boolean;
  /**
   * When true (default), `DialogHeader` stays above a scrollable `DialogBody`, and
   * `DialogFooter` is pinned to the bottom of the modal. Use `false` for legacy
   * single-column scroll (entire content scrolls together).
   */
  stickyFooter?: boolean;
};

export function DialogContent({
  children,
  className,
  showCloseButton = false,
  stickyFooter = true,
}: DialogContentProps) {
  const { onOpenChange } = useDialogContext('DialogContent');

  if (!stickyFooter) {
    return (
      <DialogLayoutContext.Provider value={{ stickyFooter: false }}>
        <div
          className={cn(
            'relative flex max-h-[min(90dvh,40rem)] min-h-0 w-full min-w-0 max-w-full flex-1 flex-col overflow-y-auto p-6',
            className,
          )}
        >
          {showCloseButton ? (
            <Button
              type='button'
              variant='ghost'
              size='icon'
              className='absolute right-3 top-3 z-10 text-muted-foreground hover:text-foreground'
              onClick={() => onOpenChange(false)}
              aria-label='Close dialog'
            >
              <Icon name='close' className='size-5' />
            </Button>
          ) : null}
          {children}
        </div>
      </DialogLayoutContext.Provider>
    );
  }

  return (
    <DialogLayoutContext.Provider value={{ stickyFooter: true }}>
      <div
        className={cn(
          'relative flex max-h-[min(90dvh,40rem)] min-h-0 w-full min-w-0 max-w-full flex-1 flex-col overflow-hidden',
          className,
        )}
      >
        {showCloseButton ? (
          <Button
            type='button'
            variant='ghost'
            size='icon'
            className='absolute right-3 top-3 z-10 text-muted-foreground hover:text-foreground'
            onClick={() => onOpenChange(false)}
            aria-label='Close dialog'
          >
            <Icon name='close' className='size-5' />
          </Button>
        ) : null}
        <div className='flex min-h-0 flex-1 flex-col'>{children}</div>
      </div>
    </DialogLayoutContext.Provider>
  );
}

export type DialogBodyProps = {
  children: ReactNode;
  className?: string;
};

/**
 * Scrollable middle region when using sticky footers (`DialogContent` default).
 * Place between `DialogHeader` and `DialogFooter`. On `<form>`, use
 * `className="flex min-h-0 flex-1 flex-col"` on the form and put `DialogBody` +
 * `DialogFooter` inside it so submit actions stay visible.
 */
export function DialogBody({ children, className }: DialogBodyProps) {
  return (
    <div
      className={cn(
        'min-h-0 flex-1 overflow-y-auto overscroll-y-contain px-6 py-2',
        className,
      )}
    >
      {children}
    </div>
  );
}

export type DialogHeaderProps = {
  children: ReactNode;
  className?: string;
};

export function DialogHeader({ children, className }: DialogHeaderProps) {
  const { stickyFooter } = useDialogLayout();

  return (
    <div
      className={cn(
        'flex flex-col gap-1.5 text-center sm:text-left',
        stickyFooter
          ? 'shrink-0 px-6 pb-2 pt-6 sm:pr-10'
          : 'sm:pr-8',
        className,
      )}
    >
      {children}
    </div>
  );
}

export type DialogFooterProps = {
  children: ReactNode;
  className?: string;
};

export function DialogFooter({ children, className }: DialogFooterProps) {
  const { stickyFooter } = useDialogLayout();

  return (
    <div
      className={cn(
        stickyFooter
          ? 'flex shrink-0 flex-col-reverse gap-2 border-t border-border bg-card px-6 pt-4 sm:flex-row sm:justify-end pb-[max(1rem,env(safe-area-inset-bottom,0px))]'
          : 'mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end',
        className,
      )}
    >
      {children}
    </div>
  );
}

export type DialogTitleProps = {
  children: ReactNode;
  className?: string;
};

export function DialogTitle({ children, className }: DialogTitleProps) {
  const { titleId } = useDialogContext('DialogTitle');

  return (
    <h2
      id={titleId}
      className={cn(
        'font-heading text-lg font-semibold tracking-tight',
        className,
      )}
    >
      {children}
    </h2>
  );
}

export type DialogDescriptionProps = {
  children: ReactNode;
  className?: string;
};

export function DialogDescription({
  children,
  className,
}: DialogDescriptionProps) {
  const { descriptionId } = useDialogContext('DialogDescription');

  return (
    <p
      id={descriptionId}
      className={cn('text-sm text-muted-foreground', className)}
    >
      {children}
    </p>
  );
}

export type DialogCloseProps = Omit<ButtonProps, 'type'>;

export function DialogClose({
  onClick,
  children = 'Close',
  ...props
}: DialogCloseProps) {
  const { onOpenChange } = useDialogContext('DialogClose');

  return (
    <Button
      type='button'
      variant='secondary'
      {...props}
      onClick={(e) => {
        onClick?.(e);
        if (!e.defaultPrevented) {
          onOpenChange(false);
        }
      }}
    >
      {children}
    </Button>
  );
}

export function Dialog({
  open,
  onOpenChange,
  children,
  className,
  'aria-label': ariaLabel,
  preventDismiss = false,
  appTheme,
  surfaceThemeStyle,
}: DialogProps) {
  const contextSurfaceTheme = useOptionalDialogSurfaceTheme();
  const dialogSurfaceTheme = appTheme ?? contextSurfaceTheme;
  const ref = useRef<HTMLDialogElement>(null);
  const onOpenChangeRef = useRef(onOpenChange);
  onOpenChangeRef.current = onOpenChange;

  const titleId = useId();
  const descriptionId = useId();

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const onClose = () => {
      onOpenChangeRef.current(false);
    };
    el.addEventListener('close', onClose);
    return () => el.removeEventListener('close', onClose);
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el || !preventDismiss) return;
    const onCancel = (e: Event) => {
      e.preventDefault();
    };
    el.addEventListener('cancel', onCancel);
    return () => el.removeEventListener('cancel', onCancel);
  }, [preventDismiss, open]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (open) {
      if (!el.open) {
        el.showModal();
      }
    } else if (el.open) {
      el.close();
    }
  }, [open]);

  const ctx: DialogContextValue = {
    onOpenChange,
    titleId,
    descriptionId,
  };

  return (
    <DialogContext.Provider value={ctx}>
      <dialog
        ref={ref}
        className={cn(
          // Shell width hugs content up to the cap (avoids a fixed 32rem panel when inner
          // DialogContent uses max-w-md / narrower — was leaving empty space on the right).
          'fixed inset-0 z-50 m-auto max-h-[min(90dvh,40rem)] w-max min-w-0 max-w-[min(100vw-2rem,32rem)] overflow-hidden rounded-2xl border border-border bg-card p-0 text-foreground shadow-xl backdrop:bg-black/50 open:flex open:flex-col',
          className,
        )}
        style={surfaceThemeStyle}
        {...(dialogSurfaceTheme
          ? { 'data-app-theme': dialogSurfaceTheme }
          : {})}
        aria-modal='true'
        aria-label={ariaLabel}
        aria-labelledby={ariaLabel ? undefined : titleId}
      >
        {children}
      </dialog>
    </DialogContext.Provider>
  );
}
